/* eslint-disable max-lines */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CheckBoxModule } from '@progress/kendo-angular-inputs';
import { ButtonComponent, SwitchComponent, MapComponent, MapService, MultiselectItem, BoundingBox } from '@ui/aos-lib';
import {
  NonNullableFormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  FormArray,
  FormControl
} from '@angular/forms';
import { AsyncPipe, CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ReportGroupsApiService } from '../../services/report-groups-api.service';
import { TextareaComponent } from '../../../event-configuration/event-configuration-edit-window/textarea/textarea.component';
import {
  Applicable,
  EditReportGroupsFormData,
  ReportGroupsCreationAPIBody,
  GroupCriteria,
  CriteriaItemForm,
  MapAreaDevicesApiRequestBody,
  UdfApiResponse,
  GroupCriteriaControlValue,
  GroupStatus
} from '../../report-groups.types';
import { catchError, EMPTY, Observable, take, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReportGroupsFilterCriteriaComponent } from '../report-groups-filter-criteria/report-groups-filter-criteria.component';
import { ReportGroupModalService, ReportGroupModalView } from './report-group-modal.service';
import { AuthService } from '@portal/auth';
import { environment } from '@portal/env';
import {
  chartHealthSegmentsColors,
  createDeviceBadgesFn
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import { FilterFieldMapping } from '../report-groups-filter-criteria/report-groups-filter-criteria.types';
import { everyFilterMustHaveValueValidator } from './report-group-modal.validator';
import { ReportGroupsDynamicFilterService } from '../report-groups-filter-criteria/report-groups-dynamic-filter/report-groups-dynamic-filter.service';
import { PermissionsService } from '../../../../../services/permissions/permissions.service';
import { PermissionValue, ResourceValue } from '../../../../../services/permissions/permissions.types';
import { ReportGroupsDynamicFilter } from '../report-groups-filter-criteria/report-groups-dynamic-filter/report-groups-dynamic-filter.types';
import { Store } from '@ngrx/store';
import {
  isSingleCommodityClient,
  selectNonCommunicationCommodities
} from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';
import { EqualsOpFilter } from '../report-groups-filter-criteria/report-groups-dynamic-filter/report-groups-dynamic-filter.const';
import { AosMapService } from '../../../../../shared/components/map/map.service';
import { MapApiService } from 'src/app/services/api/map-api.service';
import { udfSubcategory } from '../../report-groups.constants';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TranslateModule } from '@ngx-translate/core';
import { lassoDynamicFilterCommandToken } from '../report-groups-filter-criteria/report-groups-dynamic-filter/dynamic-filters/lasso-dynamic-filter.component ';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    CheckBoxModule,
    TextareaComponent,
    SwitchComponent,
    ReportGroupsFilterCriteriaComponent,
    MapComponent,
    TranslateModule,
    AsyncPipe
  ],
  selector: 'aos-report-group-modal',
  styleUrl: './report-group-modal.component.scss',
  templateUrl: './report-group-modal.component.html',
  providers: [
    ReportGroupModalService,
    {
      provide: lassoDynamicFilterCommandToken,
      useExisting: ReportGroupModalService
    }
  ]
})
export class ReportGroupCreateModalComponent implements OnInit, OnChanges {
  private fb = inject(NonNullableFormBuilder);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly permissions = inject(PermissionsService);
  private readonly mapService = inject(AosMapService);

  @Input() modalCloseSignal: () => boolean = () => false;
  @Input() editReportGroupsFormData: EditReportGroupsFormData | undefined = undefined;
  @Input() groupUID: string = '0';
  canDelete = input(true);
  @Output()
  cancelButtonClick: EventEmitter<void> = new EventEmitter();
  @Output()
  setParentModalCloseSignalToFalse: EventEmitter<void> = new EventEmitter();
  @Output()
  closeWithRefresh: EventEmitter<void> = new EventEmitter();
  reportGroupsForm!: FormGroup;

  protected defaultCoordinates!: BoundingBox;
  private pendingFocusBounds: BoundingBox | null = null;
  #uiMapService?: MapService;

  @ViewChild(MapService)
  private set uiMapService(service: MapService | undefined) {
    this.#uiMapService = service;
    if (service && this.pendingFocusBounds) {
      service.setBounds(this.pendingFocusBounds);
      this.pendingFocusBounds = null;
    } else if (!service) {
      this.pendingFocusBounds = null;
    }
  }

  protected isApiCallInProgress = false;
  protected mapAreaSelectedCoordinates: MapAreaDevicesApiRequestBody = { coordinates: [] };
  protected readonly descriptionMaxLength = 255;
  protected readonly reportGroupModalView = ReportGroupModalView;
  protected readonly hasReportGroupDeleteRight$: Observable<boolean>;
  protected readonly generalLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.TITLE.GENERAL;
  protected readonly reportGroupNameLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.REPORT_GROUP_NAME;
  protected readonly descriptionOptionalLabel =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.DESCRIPTION_OPTIONAL;
  protected readonly statusLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.STATUS;
  protected readonly applicableLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.TITLE.APPLICABLE;
  protected readonly applicableSub1Label =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.SELECT_THE_PAGES_WHERE_THE_REPORT_GROUP_WILL_BE_APPLICABLE;
  protected readonly applicableSub2Label =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.NEW_REPORT_GROUP_WILL_BE_AVAILABLE_ON;
  protected readonly enterNamePlaceholder = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.ENTER_NAME;
  protected readonly reportGroupNameExistsError =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.REPORT_GROUP_NAME_EXISTS_ERROR_MESSAGE;
  protected readonly consumptionAnalyticsLabel =
    TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.CONSUMPTION_ANALYTICS;
  protected readonly amiHealthDashboardLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.AMI_HEALTH_DASHBOARD;
  protected readonly networkMonitoringLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.FORM.NETWORK_MONITORING;
  protected readonly deleteLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.DELETE;
  protected readonly cancelLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.CANCEL;
  protected readonly backLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.BACK;
  protected readonly applyLabel = TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.APPLY;
  // todo: change API
  protected readonly tileEndpoint = environment.map.mapDevicesGeoUrl;
  // todo: change itemsCountFieldName according to an API's contract
  protected readonly itemsCountFieldName = 'deviceCount';
  protected readonly chartSegmentsColors = chartHealthSegmentsColors;
  protected readonly createBadgesFn = createDeviceBadgesFn;

  protected readonly destroyRef = inject(DestroyRef);
  private readonly reportGroupsApiService = inject(ReportGroupsApiService);
  private readonly reportGroupsDynamicFilterService = inject(ReportGroupsDynamicFilterService);
  private readonly mapApiService = inject(MapApiService);
  private readonly authService = inject(AuthService);
  protected readonly reportGroupModalService = inject(ReportGroupModalService);

  protected readonly authToken$ = this.authService.token$;
  protected udf: UdfApiResponse[] | undefined = undefined;
  readonly #clientCommodities = inject(Store).selectSignal(selectNonCommunicationCommodities);
  readonly #isSingleCommodityClient = inject(Store).selectSignal(isSingleCommodityClient);
  constructor() {
    this.hasReportGroupDeleteRight$ = this.permissions.hasRight(ResourceValue.REPORT_GROUPS, PermissionValue.DELETE);
    effect(() => {
      if (this.modalCloseSignal()) {
        this.reportGroupsForm.reset();
        this.reportGroupsForm.setControl('filterCriteria', this.fb.array([], everyFilterMustHaveValueValidator()));
        this.setParentModalCloseSignalToFalse.emit();
        this.reportGroupModalService.activateDefaultView();
      }
    });
    effect(() => {
      if (!this.isMapView()) return;
      this.mapApiService
        .getDevicesFocusArea({ filters: [[{ filterType: 'Status', filteredValues: ['Active'] }]] })
        .pipe(
          take(1),
          tap(({ northEastLatitude, northEastLongitude, southWestLatitude, southWestLongitude }) => {
            const isEmptyFocusArea =
              !northEastLatitude && !northEastLongitude && !southWestLatitude && !southWestLongitude;
            if (isEmptyFocusArea) return;
            const bounds: BoundingBox = [
              [southWestLongitude, southWestLatitude],
              [northEastLongitude, northEastLatitude]
            ];
            if (this.#uiMapService) {
              this.#uiMapService.setBounds(bounds);
            } else {
              this.pendingFocusBounds = bounds;
            }
          }),
          catchError(() => EMPTY)
        )
        .subscribe();
    });
  }

  ngOnInit(): void {
    this.defaultCoordinates = this.mapService.boundingBox;
    this.reportGroupsForm = this.fb.group({
      groupName: ['', Validators.required],
      groupDescription: [''],
      consumptionAnalytics: [false],
      amiHealthDashboard: [false],
      networkMonitoring: [false],
      isActive: [true],
      filterCriteria: this.fb.array([], everyFilterMustHaveValueValidator())
    });

    this.reportGroupsApiService
      .getUdf()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(data => {
          this.udf = data.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
          this.reportGroupsDynamicFilterService.updateFilterRegistryWithUdf(this.udf);
        })
      )
      .subscribe();

    this.reportGroupsForm
      .get('consumptionAnalytics')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isEnabled => {
        if (this.#isSingleCommodityClient()) {
          return;
        }

        const filtersArray = this.reportGroupsForm.get('filterCriteria') as FormArray;
        const commodityFilterControl = filtersArray.controls.find(
          ctrl => (ctrl?.value?.name as MultiselectItem).value === FilterFieldMapping.Commodity.value
        );

        if (commodityFilterControl) {
          const newValue = {
            ...commodityFilterControl.value,
            options: {
              ...commodityFilterControl.value.options,
              removeButtonDisabled: isEnabled
            }
          };
          return commodityFilterControl.setValue(newValue);
        }

        if (!isEnabled) {
          return;
        }

        filtersArray.push(
          this.reportGroupsDynamicFilterService.createCommodityFormGroup({ removeButtonDisabled: true })
        );
      });
  }

  ngOnChanges(): void {
    this.patchReportGroupsFormForEditing();
  }

  // TODO: consider refactoring to reduce complexity and extract duplicate code
  onSubmit(): void {
    this.isApiCallInProgress = true;
    let shouldRefresh = true;
    const payload = this.getReportGroupPayload(this.reportGroupsForm);

    if (this.editReportGroupsFormData) {
      this.reportGroupsApiService
        .updateReportGroup(this.groupUID, payload)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((error: HttpErrorResponse) => {
            this.isApiCallInProgress = false;

            if (error?.status === HttpStatusCode.Conflict) {
              shouldRefresh = false;
              this.reportGroupsForm.get('groupName')?.setErrors({ conflict: true });
            }

            return EMPTY;
          })
        )
        .subscribe({
          next: () => {
            this.isApiCallInProgress = false;
            this.reportGroupModalService.activateDefaultView();
          },
          complete: () => {
            if (shouldRefresh) {
              this.closeWithRefresh.emit();
            }
          }
        });
    } else {
      this.reportGroupsApiService
        .createReportGroup(this.groupUID, payload)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((error: HttpErrorResponse) => {
            this.isApiCallInProgress = false;

            if (error?.status === HttpStatusCode.Conflict) {
              shouldRefresh = false;
              this.reportGroupsForm.get('groupName')?.setErrors({ conflict: true });
            }

            return EMPTY;
          })
        )
        .subscribe({
          next: () => {
            this.isApiCallInProgress = false;
          },
          complete: () => {
            if (shouldRefresh) {
              this.closeWithRefresh.emit();
            }
          }
        });
    }
  }

  protected isDefaultView = computed(() => {
    return this.reportGroupModalService.modalView() === this.reportGroupModalView.Default;
  });

  protected isMapView = computed(() => {
    return this.reportGroupModalService.modalView() === this.reportGroupModalView.Lasso;
  });

  protected deleteReportGroup(): void {
    if (this.isApiCallInProgress) {
      return;
    }
    this.isApiCallInProgress = true;
    this.reportGroupsApiService
      .deleteReportGroup(this.groupUID)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.isApiCallInProgress = false;
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.isApiCallInProgress = false;
        this.closeWithRefresh.emit();
      });
  }

  protected get submitButtonText(): string {
    return this.editReportGroupsFormData
      ? TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.UPDATE_REPORT_GROUP
      : TranslateRegistry.get.CREATE_EDIT_REPORT_GROUP.BUTTON.CREATE_REPORT_GROUP;
  }

  protected markForCheck(): void {
    this.changeDetectorRef.markForCheck();
  }

  protected get isSubmitDisabled(): boolean {
    return !this.reportGroupsForm.valid || this.isApiCallInProgress || this.reportGroupsForm.pristine;
  }

  protected get isActive(): string {
    return this.reportGroupsForm.get('isActive')?.value
      ? TranslateRegistry.get.FILTER.STATUS.ACTIVE
      : TranslateRegistry.get.FILTER.STATUS.INACTIVE;
  }

  protected cancelLassoEdit(): void {
    this.reportGroupModalService.activateDefaultView();
  }

  protected applyLassoEdit(): void {
    type LassoFormGroup = FormGroup<{
      name: FormControl<MultiselectItem>;
      values?: FormControl<string[]>;
      operator?: FormControl<MultiselectItem>;
    }>;
    const filterCriteriaFormArray = this.reportGroupsForm.controls['filterCriteria'] as FormArray;

    const lassoFormCtrl: LassoFormGroup | null =
      (filterCriteriaFormArray.controls.find(
        ctrl => (ctrl as LassoFormGroup)?.controls?.name?.value?.value === FilterFieldMapping.DeviceUID.value
      ) as LassoFormGroup) ?? null;

    this.reportGroupsApiService
      .getMapAreaDevices(this.mapAreaSelectedCoordinates)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          return EMPTY;
        })
      )
      .subscribe(data => {
        if (lassoFormCtrl?.controls.values) {
          const mapResponseDevices = data.devices;
          lassoFormCtrl.controls.values.setValue(mapResponseDevices);
        }
        this.reportGroupModalService.activateDefaultView();
      });
  }

  protected onAreaSelected(selectedAreaData: [number, number][]): void {
    this.mapAreaSelectedCoordinates.coordinates = selectedAreaData.map(item => ({
      longitude: item[0],
      latitude: item[1]
    }));
  }

  protected getReportGroupPayload({
    value: {
      groupName,
      groupDescription,
      isActive,
      consumptionAnalytics,
      amiHealthDashboard,
      networkMonitoring,
      filterCriteria
    }
  }: FormGroup): ReportGroupsCreationAPIBody {
    const groupAttributes = [];

    if (consumptionAnalytics) {
      groupAttributes.push(Applicable.ConsumptionAnalytics);
    }
    if (amiHealthDashboard) {
      groupAttributes.push(Applicable.AmiHealthDashboard);
    }
    if (networkMonitoring) {
      groupAttributes.push(Applicable.NetworkMonitoring);
    }

    const payload = {
      groupName,
      groupDescription,
      isActive,
      ...(groupAttributes.length ? { groupAttributes: [{ name: 'Applicable', values: groupAttributes }] } : {}),
      groupCriteria: this.prepareGroupCriteriaApiPayload(filterCriteria)
    };

    return payload;
  }

  private prepareGroupCriteriaApiPayload = (data: CriteriaItemForm[]): GroupCriteria[] => {
    const emptyFormControlName = 'Select';
    const groupCriteria = data
      .filter(item => item.name.text !== emptyFormControlName)
      .map(item => ({
        name: item.name.value,
        operator: item.operator.value,
        attributeType: item.name.attributeType,
        values: this.getGroupCriteriaControlValueForApi(item.name.value, item.values, item.operator.value)
      }));

    return this.#isSingleCommodityClient()
      ? [
          ...this.#getGroupCriteriaFilteredByCommodity(groupCriteria!),
          {
            name: FilterFieldMapping.Commodity.value as string,
            operator: EqualsOpFilter.value,
            attributeType: FilterFieldMapping.Commodity.attributeType,
            values: this.#clientCommodities()
          }
        ]
      : groupCriteria;
  };

  private getGroupCriteriaControlValueForApi(
    controlName: string,
    values: GroupCriteriaControlValue,
    operator: string
  ): string[] {
    //todo: refactor if-else control using Filter#createApiPayloadFromControlValue
    const filter = this.reportGroupsDynamicFilterService.findFilter(controlName) as ReportGroupsDynamicFilter;
    if (filter.createApiPayloadFromControlValue) {
      return filter?.createApiPayloadFromControlValue(values as string[], operator) as string[];
    }

    if (typeof values === 'string') {
      return operator === 'IN' ? values.split(',').map(v => v.trim()) : [values];
    } else if (this.reportGroupsDynamicFilterService.isCheckboxControl(controlName)) {
      return values
        .filter(item => Object.values(item)[0] === true)
        .map(item => Object.keys(item)[0])
        .map(item => this.reportGroupsDynamicFilterService.getDeviceTypeMappedValueForApi(item));
    } else if (Array.isArray(values) && values.length > 0) {
      return values as string[];
    }
    return [];
  }

  private patchReportGroupsFormForEditing(): void {
    if (this.editReportGroupsFormData && this.reportGroupsForm) {
      const { groupName, groupDescription, groupStatus, applicable, groupCriteria } = this.editReportGroupsFormData;
      this.reportGroupsForm.patchValue({
        groupName,
        groupDescription,
        consumptionAnalytics: applicable?.includes(Applicable.ConsumptionAnalytics) ? true : false,
        amiHealthDashboard: applicable?.includes(Applicable.AmiHealthDashboard) ? true : false,
        networkMonitoring: applicable?.includes(Applicable.NetworkMonitoring) ? true : false,
        isActive: groupStatus === GroupStatus.Active ? true : false
      });
      this.setFilterCriteriaForEditing(
        this.#isSingleCommodityClient() ? this.#getGroupCriteriaFilteredByCommodity(groupCriteria!) : groupCriteria
      );
      this.reportGroupsForm.markAsPristine();
    }
  }

  private setFilterCriteriaForEditing(groupCriteria: GroupCriteria[] | undefined): void {
    this.reportGroupsForm.setControl('filterCriteria', this.fb.array([], everyFilterMustHaveValueValidator()));
    const filtersArray = this.reportGroupsForm.get('filterCriteria') as FormArray;
    const isConsumptionAnalyticsEnabled: boolean = this.reportGroupsForm.get('consumptionAnalytics')?.value;

    groupCriteria?.forEach(({ name, operator, values, attributeType }) => {
      const preparedText = FilterFieldMapping[name]?.text ?? name;
      const preparedValue = FilterFieldMapping[name]?.value ?? name;
      const nameValue = [
        {
          text: preparedText,
          value: preparedValue,
          subcategory: FilterFieldMapping[name]?.subcategory ?? udfSubcategory,
          fieldName: FilterFieldMapping[name]?.fieldName ?? name,
          attributeType
        }
      ];

      filtersArray.push(
        this.fb.group({
          name: nameValue,
          operator: [{ text: operator, value: operator }],
          values: this.reportGroupsDynamicFilterService.getGroupCriteriaControlValueFromApi(name, values),
          /* todo: the options should be created dynamically based on the formName
            const options = this.reportGroupsDynamicFilterService.findFilter(name)?.controls.find(ctrlFn => {
              return ctrlFn().name === 'options';
            });
            Also it would be better if logic above was encapsulated within a function, e.g. this.reportGroupsDynamicFilterService.findFilterOptions(formName)
            Questions: how to apply outside logic to create a dynamic options, based on for example a value from the ConsumptionAnalytics control?
            Options: use middleware pattern, having some general function with ctx (e.g. this.reportGroupsForm) passed to the
                      side Effect Functions of each filter model
          */
          options: [
            ...(name === FilterFieldMapping.Commodity.value
              ? [
                  {
                    removeButtonDisabled: isConsumptionAnalyticsEnabled,
                    removeButtonTooltip: 'The filter by Commodity is required when Consumption Analytics is selected'
                  }
                ]
              : [])
          ]
        })
      );
    });
  }

  #getGroupCriteriaFilteredByCommodity(groupCriteria: GroupCriteria[]): GroupCriteria[] {
    return groupCriteria?.filter(item => item.name !== FilterFieldMapping.Commodity.value);
  }
}
